/**
 * cues-music.ts — procedural end-game music stingers (v3M12.3).
 *
 * Implements the AUDIO §5.1 victory + defeat one-shot stingers per
 * `docs/WIN_CONDITIONS.md` §6. Both route through the music bus and
 * are NOT loops — they play once when `gameOutcome` flips, layered
 * over silence (ambient bed faded out 1 s prior). Other music beds
 * (menu, ambient, combat overlay) and the §5.2 combat-overlay state
 * machine all defer to v3M11 polish.
 *
 *   - **`music:victory`** — ascending C-major arpeggio (C5 → E5 → G5
 *     → C6), 2.4 s primary phrase, ~5 s reverb-style tail with a
 *     softer detuned third-and-fifth bell-cluster sustain. Total ~7 s.
 *   - **`music:defeat`** — descending C-minor melody (C5 → A♭4 → F4
 *     → C4), 3 s primary, ~7 s sub-bass drone (C2) fading in toward
 *     the end. Total ~10 s.
 *
 * Forward-compat to v3M11 polish: `engine.registry.registerSample(
 *   'music:victory', commissionedBuffer)` swaps the procedural for a
 * commissioned ambient-electronic composition with no caller change.
 */

import type { AudioEngine } from './engine';
import type { CueVariation } from './registry';

const NEUTRAL: CueVariation = { pitchMul: 1, gainMul: 1 };

export function registerMusicCues(engine: AudioEngine): void {
  engine.registry.registerProcedural('music:victory', 'music', buildMusicVictory);
  engine.registry.registerProcedural('music:defeat', 'music', buildMusicDefeat);
}

/**
 * Victory — 4-note ascending C-major arpeggio with a long reverb-
 * style tail. Each note is a sine wave with a bell-like envelope
 * (sharp attack, slow decay). Notes layer rather than replace so
 * the arpeggio reads as a chord building.
 */
function buildMusicVictory(
  ctx: AudioContext,
  target: AudioNode,
  variation: CueVariation = NEUTRAL,
): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  // C major: C5 / E5 / G5 / C6 (Hz, equal temperament).
  const arpeggio = [523.25, 659.25, 783.99, 1046.5];
  const noteSpacing = 0.6; // seconds between note starts
  const noteDecay = 1.2; // exponential decay per note
  const peakGain = 0.32 * variation.gainMul;

  // Primary note — the first arpeggio voice. Engine starts this; the
  // remaining voices start themselves on schedule.
  const primary = makeNote(
    ctx,
    target,
    arpeggio[0] * variation.pitchMul,
    now,
    peakGain,
    noteDecay,
  );
  for (let i = 1; i < arpeggio.length; i++) {
    const startAt = now + i * noteSpacing;
    const note = makeNote(
      ctx,
      target,
      arpeggio[i] * variation.pitchMul,
      startAt,
      peakGain,
      noteDecay,
    );
    note.start(startAt);
    note.stop(startAt + noteDecay + 0.05);
  }

  // Reverb-style tail — a sustained chord cluster (C5 + E5 + G5 + C6
  // detuned by ±5 cents) entering after the arpeggio peaks and decaying
  // over ~5 s. Voices below the peak gain so the cluster sits as a
  // wash rather than a foreground statement.
  const tailStart = now + arpeggio.length * noteSpacing - 0.2;
  const tailGain = 0.12 * variation.gainMul;
  const tailDecay = 4.5;
  const tailDetunes = [-5, 5, -3, 3];
  for (let i = 0; i < arpeggio.length; i++) {
    const tail = ctx.createOscillator();
    tail.type = 'sine';
    tail.frequency.value = arpeggio[i] * variation.pitchMul;
    tail.detune.value = tailDetunes[i];
    const tailEnv = ctx.createGain();
    tailEnv.gain.setValueAtTime(0.0001, tailStart);
    tailEnv.gain.exponentialRampToValueAtTime(tailGain, tailStart + 0.5);
    tailEnv.gain.exponentialRampToValueAtTime(0.0001, tailStart + tailDecay);
    tail.connect(tailEnv);
    tailEnv.connect(target);
    tail.start(tailStart);
    tail.stop(tailStart + tailDecay + 0.05);
  }

  return primary;
}

/**
 * Defeat — 4-note descending C-minor melody with a low sub-bass drone
 * fading in toward the end. Notes are slower-decay sines, drone is a
 * triangle at C2 (65.4 Hz) so it has a touch of warmth rather than
 * pure-sine sterility.
 */
function buildMusicDefeat(
  ctx: AudioContext,
  target: AudioNode,
  variation: CueVariation = NEUTRAL,
): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  // C minor descending: C5 / A♭4 / F4 / C4.
  const melody = [523.25, 415.3, 349.23, 261.63];
  const noteSpacing = 0.75;
  const noteDecay = 1.8;
  const peakGain = 0.3 * variation.gainMul;

  const primary = makeNote(
    ctx,
    target,
    melody[0] * variation.pitchMul,
    now,
    peakGain,
    noteDecay,
  );
  for (let i = 1; i < melody.length; i++) {
    const startAt = now + i * noteSpacing;
    const note = makeNote(
      ctx,
      target,
      melody[i] * variation.pitchMul,
      startAt,
      peakGain,
      noteDecay,
    );
    note.start(startAt);
    note.stop(startAt + noteDecay + 0.05);
  }

  // Sub-bass drone — fades in over the last 3 s of the melody, sustains
  // for another 4 s past the final note. Triangle wave at C2 with a
  // gentle envelope.
  const droneStart = now + melody.length * noteSpacing - 1.5;
  const droneEnd = droneStart + 6.0;
  const drone = ctx.createOscillator();
  drone.type = 'triangle';
  drone.frequency.value = 65.4 * variation.pitchMul;
  const droneEnv = ctx.createGain();
  droneEnv.gain.setValueAtTime(0.0001, droneStart);
  droneEnv.gain.exponentialRampToValueAtTime(
    0.22 * variation.gainMul,
    droneStart + 2.0,
  );
  droneEnv.gain.setValueAtTime(0.22 * variation.gainMul, droneEnd - 1.5);
  droneEnv.gain.exponentialRampToValueAtTime(0.0001, droneEnd);
  drone.connect(droneEnv);
  droneEnv.connect(target);
  drone.start(droneStart);
  drone.stop(droneEnd + 0.05);

  return primary;
}

/**
 * Build a single bell-like sine note at `freq`, scheduled to start at
 * `startAt` with `peak` gain and an exponential `decay` (seconds).
 * Connects the note's envelope to `target` ready for `start()` to be
 * called by the caller — for the primary note that's the engine; for
 * trailing notes it's this file's own `start(startAt)` call.
 */
function makeNote(
  ctx: AudioContext,
  target: AudioNode,
  freq: number,
  startAt: number,
  peak: number,
  decay: number,
): AudioScheduledSourceNode {
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.value = freq;
  const env = ctx.createGain();
  env.gain.setValueAtTime(0.0001, startAt);
  env.gain.exponentialRampToValueAtTime(peak, startAt + 0.04);
  env.gain.exponentialRampToValueAtTime(0.0001, startAt + decay);
  osc.connect(env);
  env.connect(target);
  // The primary note relies on the engine's `source.start()`, which
  // calls `start()` with no argument (= currentTime). For that to land
  // at the right moment, the primary's `startAt` must equal `now` at
  // build time — true above.
  return osc;
}
